/**
 * Wave 2.5 — Admin clause dictionary (FL/VA packs + custom).
 * Additive management UI; does not alter residential hardcoded clauses.
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import {
  useApproveAdminClauseDictionaryMutation,
  useArchiveAdminClauseDictionaryMutation,
  useCreateAdminClauseDictionaryMutation,
  useEnsureAdminClauseDictionarySeedMutation,
  useGetAdminClauseDictionaryQuery,
  useUpdateAdminClauseDictionaryMutation,
  type ClauseDictionaryEntry,
} from '@/apis/extended/clauseDictionaryApi';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { TasteSectionCard } from '@/components/Common/TasteSectionCard';

const emptyForm = {
  clauseId: '',
  title: '',
  description: '',
  jurisdictions: 'FL,VA',
  propertyTypes: 'Commercial',
  requiredByState: false,
  isNegotiable: true,
  isRequired: false,
  defaultValue: '',
  contractFieldPath: '',
  category: 'commercial',
};

export const AdminClausesPage = () => {
  const { data = [], isLoading, refetch } = useGetAdminClauseDictionaryQuery();
  const [ensureSeed, { isLoading: seeding }] =
    useEnsureAdminClauseDictionarySeedMutation();
  const [createEntry, { isLoading: creating }] =
    useCreateAdminClauseDictionaryMutation();
  const [updateEntry] = useUpdateAdminClauseDictionaryMutation();
  const [approveEntry] = useApproveAdminClauseDictionaryMutation();
  const [archiveEntry] = useArchiveAdminClauseDictionaryMutation();

  const [filter, setFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (r) =>
        r.clauseId.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        (r.jurisdictions || []).join(',').toLowerCase().includes(q)
    );
  }, [data, filter]);

  const handleSeed = async () => {
    try {
      await ensureSeed().unwrap();
      toast.success('FL/VA commercial seed ensured');
      refetch();
    } catch {
      toast.error('Could not seed dictionary');
    }
  };

  const handleCreate = async () => {
    if (!form.clauseId.trim() || !form.title.trim()) {
      toast.error('clauseId and title are required');
      return;
    }
    try {
      await createEntry({
        clauseId: form.clauseId.trim(),
        title: form.title.trim(),
        description: form.description,
        jurisdictions: form.jurisdictions
          .split(/[,\s]+/)
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
        propertyTypes: form.propertyTypes
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        requiredByState: form.requiredByState,
        isNegotiable: form.isNegotiable,
        isRequired: form.isRequired,
        defaultValue: form.defaultValue,
        contractFieldPath: form.contractFieldPath,
        category: form.category,
        status: 'active',
      }).unwrap();
      toast.success('Clause created');
      setFormOpen(false);
      setForm(emptyForm);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'data' in e
          ? String((e as { data?: { error?: string } }).data?.error)
          : 'Create failed';
      toast.error(msg || 'Create failed');
    }
  };

  const toggleRequiredByState = async (row: ClauseDictionaryEntry) => {
    try {
      await updateEntry({
        id: row._id,
        body: { requiredByState: !row.requiredByState },
      }).unwrap();
      toast.success('Updated');
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <Box>
      <TastePageHeader
        title="Clause dictionary"
        subtitle="Manage FL/VA commercial packs and custom clauses. Residential negotiation still uses the existing built-in clause set — this dictionary is additive."
        actions={
          <>
            <Button variant="outlined" onClick={handleSeed} disabled={seeding}>
              Ensure FL/VA seed
            </Button>
            <Button variant="contained" onClick={() => setFormOpen(true)}>
              Add clause
            </Button>
          </>
        }
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack>

      <TasteSectionCard>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>clauseId</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>States</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Required by state</TableCell>
              <TableCell>Negotiable</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  No clauses yet. Click “Ensure FL/VA seed”.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {row.clauseId}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>
                    {(row.jurisdictions || []).map((j) => (
                      <Chip key={j} size="small" label={j} sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status || 'active'}
                      color={
                        row.status === 'proposed'
                          ? 'warning'
                          : row.status === 'archived'
                          ? 'default'
                          : 'success'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={Boolean(row.requiredByState)}
                      onChange={() => void toggleRequiredByState(row)}
                    />
                  </TableCell>
                  <TableCell>
                    {row.isNegotiable === false ? 'No' : 'Yes'}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={0.5} justifyContent="flex-end">
                      {row.status === 'proposed' && (
                        <Button
                          size="small"
                          onClick={async () => {
                            try {
                              await approveEntry(row._id).unwrap();
                              toast.success('Approved');
                            } catch {
                              toast.error('Approve failed');
                            }
                          }}
                        >
                          Approve
                        </Button>
                      )}
                      {row.status !== 'archived' && (
                        <Button
                          size="small"
                          color="warning"
                          onClick={async () => {
                            try {
                              await archiveEntry(row._id).unwrap();
                              toast.success('Archived');
                            } catch {
                              toast.error('Archive failed');
                            }
                          }}
                        >
                          Archive
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TasteSectionCard>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add dictionary clause</DialogTitle>
        <DialogContent>
          <Stack gap={1.5} sx={{ mt: 1 }}>
            <TextField
              label="clauseId"
              value={form.clauseId}
              onChange={(e) =>
                setForm((f) => ({ ...f, clauseId: e.target.value }))
              }
              helperText="kebab-case, e.g. commercial-cap-ex"
            />
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
            />
            <TextField
              label="Description"
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
            <TextField
              label="Jurisdictions"
              value={form.jurisdictions}
              onChange={(e) =>
                setForm((f) => ({ ...f, jurisdictions: e.target.value }))
              }
              helperText="Comma-separated, e.g. FL,VA"
            />
            <TextField
              label="Property types"
              value={form.propertyTypes}
              onChange={(e) =>
                setForm((f) => ({ ...f, propertyTypes: e.target.value }))
              }
            />
            <TextField
              select
              label="Category"
              value={form.category}
              onChange={(e) =>
                setForm((f) => ({ ...f, category: e.target.value }))
              }
            >
              <MenuItem value="commercial">commercial</MenuItem>
              <MenuItem value="legal">legal</MenuItem>
              <MenuItem value="other">other</MenuItem>
            </TextField>
            <TextField
              label="Default value"
              multiline
              minRows={2}
              value={form.defaultValue}
              onChange={(e) =>
                setForm((f) => ({ ...f, defaultValue: e.target.value }))
              }
            />
            <TextField
              label="Contract field path (optional)"
              value={form.contractFieldPath}
              onChange={(e) =>
                setForm((f) => ({ ...f, contractFieldPath: e.target.value }))
              }
              helperText="e.g. commercialTerms.customField"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.requiredByState}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      requiredByState: e.target.checked,
                    }))
                  }
                />
              }
              label="Required by state pack"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isNegotiable}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isNegotiable: e.target.checked }))
                  }
                />
              }
              label="Negotiable"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleCreate()}
            disabled={creating}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
